/**
 * createTableInteractions.ts - The table's user operations, exposed on the model as
 * `TableModel.interactions`.
 *
 * The table is layered as:
 * - the component (`TableEditable`): decodes events, owns the React lifecycle and modals;
 * - `model.interactions` (this module): table-wide user operations such as selecting, editing,
 *   pasting, dragging, filling down and freezing. They may touch the DOM, focus, scroll, the cell
 *   editor position and warnings, and return nothing or a status, never DOM or raw data;
 * - `model.mutations`: data and state writes, with no DOM.
 *
 * The interactions are built once per table by `TableModelProvider`. Every method reads the latest
 * model, props and mutations from the store when called, so the object and its methods keep their
 * identity for the table's lifetime even when the mutations are rebuilt.
 *
 * DOM access, element refs, timers and scrolling are private sub-modules composed here: `tableDom`
 * (read-only DOM lookups), `tableScroller` (scrolling and scheduled retries), `cellEditorSession`,
 * `tableSelection`, `pointerGestures` and `tableClipboard`. The elements live in a private registry
 * that the component fills through the write-only {@link TableElementAttachments}, and the table counts
 * as mounted while its focus bearer is attached.
 * Do not add getters that return DOM or live model data. {@link TableInteractions.classBridge} is the
 * one exception, a temporary workaround for `TableEditableClass` while it is being detangled.
 */

import type React from 'react';
import type {TableVirtuosoHandle} from 'react-virtuoso';

import {assert} from 'Asserts';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableDimension, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {
  showFreezeMergeConflictToast,
  wouldFreezeExceedViewport,
} from 'notebook/tables/core-ui/FreezeColumnUtils';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import {createCellEditorSession} from 'notebook/tables/store/logic/interactions/cellEditorSession';
import {
  AxisCellDragStartArgs,
  createPointerGestures,
} from 'notebook/tables/store/logic/interactions/pointerGestures';
import {
  createTableClipboard,
  TablePasteOptions,
} from 'notebook/tables/store/logic/interactions/tableClipboard';
import {
  createTableDom,
  TableElements,
  TableVerticalScrollViews,
} from 'notebook/tables/store/logic/interactions/tableDom';
import {createTableScroller} from 'notebook/tables/store/logic/interactions/tableScroller';
import {
  CellDragStartArgs,
  createTableSelection,
} from 'notebook/tables/store/logic/interactions/tableSelection';
import {computeCellCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import {ElementClipboardEvent, ElementMouseEvent, Optional} from 'util/Types';

export type {AxisCellDragStartArgs} from 'notebook/tables/store/logic/interactions/pointerGestures';
export type {TablePasteOptions} from 'notebook/tables/store/logic/interactions/tableClipboard';
export {
  type CellDragStartArgs,
  compareTableIndex,
  type MultiEditingUpdate,
  setTableMultiEditing,
  tryInsertIntoTable,
} from 'notebook/tables/store/logic/interactions/tableSelection';

/**
 * The handlers that cells, axis cells and the header row read from their model's `interactions`.
 * Every method keeps its identity for the table's lifetime and does nothing while the table is not
 * mounted.
 */
export interface TableInteractionHandle {
  /** Starts (or shift-extends) a whole row or column selection from a mouse down on an axis cell. */
  axisCellDragStart: (options: AxisCellDragStartArgs, e: ElementMouseEvent) => void;
  /** Extends the in-progress row or column selection to the axis cell under the pointer. */
  axisCellDragOver: (index: TableIndex, e: ElementMouseEvent) => void;
  /**
   * Starts dragging the selected rows or columns to reorder them, selecting just the dragged one
   * when it is not already selected. Returns whether the drag started.
   */
  axisCellDragHandleMouseDown: (draggingDimension: TableDimension, index: number) => boolean;
  /**
   * Finalizes any uncommitted text so that no cell is in edit mode, keeping the selection. Safe to
   * call when there is no active edit.
   */
  finalizeAndExitEditMode: () => void;
  /** Freezes the columns up to and including column `j`, unless the next column would not fit. */
  freezeThroughColumn: (j: number) => void;
  /** Unfreezes the columns from column `j` onward. */
  unfreezeFromColumn: (j: number) => void;
  /** Starts (or shift-extends) a selection from a mouse down on a cell. */
  cellDragStart: (args: CellDragStartArgs, e: ElementMouseEvent) => void;
  /** Extends the in-progress drag selection to the cell under the pointer. */
  cellDragOver: (index: TableIndex) => void;
  /** Resizes the in-progress fill-down box to reach the cell under the pointer. */
  cellMouseMoveHandler: (index: TableIndex) => void;
  /** Starts a fill-down drag from the selection's fill-down square. */
  fillDownSquareMouseDownHandler: (e: ElementMouseEvent) => void;
  /** Fills the selection down through the following blank rows. */
  fillDownSquareDoubleClickHandler: (e: ElementMouseEvent) => void;
  /**
   * Finalizes any in-progress edit, then selects the cell at `index`, starting an edit there (and
   * positioning the cell editor over it) when `options.editing` is set.
   */
  selectCell: (index: TableIndex, options: MultiSelectListUtil.MultiSelectSingleParam<TableIndex>) => void;
  /**
   * Starts editing the cell at `index`, first growing the table to contain it when it is a ghost
   * cell. Places the caret at the end of the cell's text, or selects all of it when `selectAll` is
   * set. Warns and does nothing when the selection is locked.
   */
  expandToAndEditCell: (index: TableIndex, selectAll?: boolean) => void;
  /**
   * Pastes the clipboard into the table at the selection: a pasted image is attached to the selected
   * cell when it accepts files, and anything else is parsed as a table (HTML when the clipboard holds
   * a single table, otherwise tab-separated text). Must not be called while the table is locked.
   */
  multiPasteHandler: (e: ElementClipboardEvent, options?: TablePasteOptions) => void;
}

/**
 * Write-only, stable callback refs that register the table's elements with the interactions. Render
 * them as `ref={interactions.attach.x}` (or call them from a ref the component also keeps).
 */
export interface TableElementAttachments {
  /** The table counts as mounted while its focus bearer is attached. */
  focusBearer: React.RefCallback<HTMLDivElement>;
  horizontalScrollContainer: React.RefCallback<HTMLDivElement>;
  stickyHeader: React.RefCallback<HTMLDivElement>;
  virtualizationHandle: React.RefCallback<TableVirtuosoHandle>;
  tableRoot: React.RefCallback<HTMLDivElement>;
  /** Called by `useTableMeasurement` whenever it finds (or drops) the page's vertical scroll container. */
  pageVerticalScrollContainer: (container: Element | null) => void;
}

/** The scrolling and cell editor operations that `TableEditableClass` still calls directly. */
export interface TableViewport {
  /**
   * Positions the cell editor over the cell at `index`. When the cell is not rendered or is out of
   * view, scrolls it into view instead and retries once the scroll settles, while the edit is still
   * active and the editor has not been positioned.
   */
  setTableCellEditorPosition: (index: TableIndex) => void;
  /** Scrolls the table and page so the cell at `index` is visible below the sticky header. */
  scrollCellIntoView: (index: TableIndex) => void;
  /** Scrolls the page to center the table when it is entirely out of view. */
  scrollTableIntoView: () => void;
  /**
   * Clears the cell editor's position after an edit, first scrolling back to the edited cell when the
   * user scrolled away from it during the edit.
   */
  scrollCellIntoViewAfterEdit: () => void;
  /** Extends the in-progress drag to the cell left under the pointer by a scroll. */
  scrollCompleteHandler: () => void;
  queryCellElement: (index: TableIndex) => Element | null;
  /** The container that scrolls the table vertically: its own scroll area when height-limited. */
  getTableVerticalScrollContainer: () => HTMLElement | null;
  /**
   * `getTableVerticalScrollContainer` for the given `views`, so render can pass its subscribed
   * snapshot instead of reading the live model.
   */
  getTableVerticalScrollContainerForViews: (views: TableVerticalScrollViews) => HTMLElement | null;
  getPageVerticalScrollContainer: () => Element | null;
  getSingleInputGroupHeight: () => number;
}

/**
 * What `TableEditableClass` still reaches into while its keyboard, formula box, scroll and render code
 * is detangled. A temporary workaround: nothing new should depend on it.
 */
export interface TableInteractionClassBridge {
  viewport: TableViewport;
  /**
   * Selects the cell at `index`. When `param.editing` is set, enters edit mode and positions the cell
   * editor over the cell.
   */
  multiSelectSingle: (
    index: TableIndex,
    param: MultiSelectListUtil.MultiSelectSingleParam<TableIndex> | undefined
  ) => void;
  /** Sets the selection's anchor and end, syncs the anchor's editing state and focuses the table. */
  setMultiIndices: (
    indices: {start: TableIndex; end: TableIndex; text?: string},
    options?: {multiSelIndex?: TableIndex | null}
  ) => void;
  /** Grows the table to contain `index` and returns the (empty) cell now there. */
  expandToCell: (index: TableIndex) => Cell;
}

/** The table's user operations. See the module docstring for the rules they follow. */
export interface TableInteractions extends TableInteractionHandle {
  /**
   * Commits the in-progress cell edit, if there is one. Unlike the other interactions this also runs
   * while the table is unmounting, so that an edit is not lost when the table goes away.
   */
  finalizeEdit: () => void;
  /** Records the pointer position over the table's scroll area, for drags that continue while scrolling. */
  trackPointer: (clientX: number, clientY: number) => void;
  attach: TableElementAttachments;
  /** @deprecated A temporary workaround for `TableEditableClass`; see {@link TableInteractionClassBridge}. */
  classBridge: TableInteractionClassBridge;
}

export interface TableInteractionParams {
  /** Reads the latest {@link TableModel} from the store. Only call from handlers. */
  getTableModel: () => TableModel;
  /** Reads the latest {@link TableProps} from the store. Only call from handlers. */
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
}

export function createTableInteractions({
  getTableModel,
  getTableProps,
  setState,
}: TableInteractionParams): TableInteractions {
  const elements: TableElements = {
    tableRoot: null,
    focusBearer: null,
    horizontalScrollContainer: null,
    pageVerticalScrollContainer: null,
    stickyHeader: null,
    virtualizationHandle: null,
  };
  const isMounted = (): boolean => elements.focusBearer != null;
  const focusBearer = (): HTMLDivElement => elements.focusBearer!;

  const dom = createTableDom({elements, getTableModel, getTableProps});
  const scroller = createTableScroller({dom, getTableModel, getTableProps, isMounted});
  const scrollSettled = (): void => {
    gestures.scrollSettled();
  };
  const editor = createCellEditorSession({
    dom,
    scroller,
    getTableModel,
    getTableProps,
    setState,
    isMounted,
    onScrollSettled: scrollSettled,
  });
  const selection = createTableSelection({
    getTableModel,
    getTableProps,
    setState,
    isMounted,
    focusBearer,
    finalizeEdit: editor.finalizeEdit,
    positionEditor: editor.positionEditor,
  });
  const gestures = createPointerGestures({
    dom,
    selection,
    getTableModel,
    getTableProps,
    setState,
    isMounted,
    focusBearer,
  });
  const clipboard = createTableClipboard({
    selection,
    getTableModel,
    getTableProps,
    isMounted,
    finishEditorAfterEdit: editor.finishEditorAfterEdit,
  });

  const viewport: TableViewport = {
    setTableCellEditorPosition: editor.positionEditor,
    scrollCellIntoView: (index) => {
      scroller.scrollCellIntoView(index, scrollSettled);
    },
    scrollTableIntoView: scroller.scrollTableIntoView,
    scrollCellIntoViewAfterEdit: editor.finishEditorAfterEdit,
    scrollCompleteHandler: scrollSettled,
    queryCellElement: dom.findCellElement,
    getTableVerticalScrollContainer: () => dom.getTableVerticalScrollContainer(getTableModel().views),
    getTableVerticalScrollContainerForViews: dom.getTableVerticalScrollContainer,
    getPageVerticalScrollContainer: () => elements.pageVerticalScrollContainer,
    getSingleInputGroupHeight: dom.getSingleInputGroupHeight,
  };

  const finalizeAndExitEditMode = (): void => {
    if (!isMounted()) {
      return;
    }
    const {multiSelIndex} = getTableModel().state;
    if (!multiSelIndex) {
      return;
    }
    editor.finalizeEdit();
    selection.select(multiSelIndex, {
      collapse: false, // preserve selection
      editing: false, // but exit edit mode
    });
    setCellEditorPosition(null);
  };

  const handle: TableInteractionHandle = {
    axisCellDragStart: gestures.pointerDownOnAxis,
    axisCellDragOver: gestures.pointerOverAxis,
    axisCellDragHandleMouseDown: gestures.startReorder,

    finalizeAndExitEditMode,

    freezeThroughColumn: (j) => {
      if (!isMounted()) {
        return;
      }
      const result = getTableModel().mutations.freezeThroughColumn(j, {
        canFreeze: ({widths}, siBoundary) => {
          const container = elements.horizontalScrollContainer;
          if (container && widths && widths.length > 0) {
            if (wouldFreezeExceedViewport(widths, siBoundary, j, container.clientWidth)) {
              ToastActions.pushToast({
                message: 'Cannot freeze: not enough room to display the next column.',
                type: ToastType.WARNING,
              });
              return false;
            }
          }
          return true;
        },
        beforePersist: finalizeAndExitEditMode,
      });
      if (result === 'merge-conflict') {
        showFreezeMergeConflictToast('freeze');
      }
    },

    unfreezeFromColumn: (j) => {
      if (!isMounted()) {
        return;
      }
      const result = getTableModel().mutations.unfreezeFromColumn(j, {
        beforePersist: finalizeAndExitEditMode,
      });
      if (result === 'merge-conflict') {
        showFreezeMergeConflictToast('unfreeze');
      }
    },

    cellDragStart: gestures.pointerDownOnCell,
    cellDragOver: gestures.pointerOverCell,
    cellMouseMoveHandler: gestures.pointerMoveOverCell,
    fillDownSquareMouseDownHandler: gestures.startFillDown,
    fillDownSquareDoubleClickHandler: gestures.fillDownToBlankRows,

    selectCell: (index, options) => {
      if (!isMounted()) {
        return;
      }
      editor.finalizeEdit();
      selection.select(index, options);
    },

    expandToAndEditCell: (index, selectAll = false) => {
      if (!isMounted()) {
        return;
      }
      const {locking} = getTableModel();
      if (locking.lockedWarningMessage) {
        notifications.warn(locking.lockedWarningMessage, {id: 'TableEditable.isLockedWithEditWarn'});
      }
      if (locking.isCellSelectionLocked) {
        return;
      }

      const [i, j] = index;
      let text: string | null;
      let typedValue: Optional<TypedValue>;
      let end: number;
      const {table, cid} = getTableProps();
      if (computeIsRealIndex(index, table)) {
        text = null;
        typedValue = null;
        end = table.table[i][j].text.length;
      } else {
        // A ghost cell: grow the table to reach it, which leaves an empty cell (keeping the precision
        // of the cell above when the table grows by rows).
        ({text, typedValue} = getTableModel().mutations.expandToCell(index));
        end = text.length;
      }

      selection.select(index, {
        text,
        typedValue: typedValue || null,
        editing: true,
        collapse: false,
        selection: {cid: computeCellCid(cid, i, j), start: selectAll ? 0 : end, end},
      });
    },

    multiPasteHandler: clipboard.paste,
  };

  const attach: TableElementAttachments = {
    focusBearer: (element) => {
      elements.focusBearer = element;
      if (element == null) {
        scroller.cancelPendingWork();
        gestures.stop();
      }
    },
    horizontalScrollContainer: (element) => {
      elements.horizontalScrollContainer = element;
    },
    stickyHeader: (element) => {
      elements.stickyHeader = element;
    },
    virtualizationHandle: (handleInstance) => {
      elements.virtualizationHandle = handleInstance;
    },
    tableRoot: (element) => {
      elements.tableRoot = element;
    },
    pageVerticalScrollContainer: (container) => {
      elements.pageVerticalScrollContainer = container;
    },
  };

  return {
    ...handle,
    finalizeEdit: editor.finalizeEdit,
    trackPointer: gestures.trackPointer,
    attach,
    classBridge: {
      viewport,
      multiSelectSingle: selection.select,
      setMultiIndices: ({start, end, text}, options = {}) => {
        selection.selectRange(start, end, {text, multiSelIndex: options.multiSelIndex});
      },
      expandToCell: (index) => getTableModel().mutations.expandToCell(index),
    },
  };
}

/**
 * The interactions of a table rendered without a `TableEditable` (an isolated store in a test, or a
 * standalone axis cell). Nothing ever attaches a focus bearer to them, so every interaction does
 * nothing.
 */
export const UNAVAILABLE_TABLE_INTERACTIONS: TableInteractions = createTableInteractions({
  getTableModel: () => {
    assert(false, 'This table has no interactions');
  },
  getTableProps: () => {
    assert(false, 'This table has no interactions');
  },
  setState: () => {
    assert(false, 'This table has no interactions');
  },
});
